import * as React from 'react'

import {Label} from '@frontrow/console/src/components/ui/label'

import {mmap} from '@freckle/maybe'
import {cn} from '@frontrow/console/src/lib/utils'
import {exhaustive} from '@freckle/exhaustive'
import {HttpError} from '@frontrow/console/src/exceptions/http-error'
import {
  createSelectorOptionAsItem,
  type SelectorItemT
} from '@frontrow/console/src/components/generic-selector'

import {Selector} from '@frontrow/console/src/components/self-contained-teacher-selector/selector'
import {teacherSelector} from './self-contained-teacher-selector.module.scss'

type ComponentLabelsT = {
  formLabel?: string | null
  loadingText: string
}

export type FetchedOrBeFetchedT<T> =
  | {
      tag: 'fetched'
      value: T
    }
  | {
      tag: 'be-fetched'
      fetch: () => Promise<T>
    }

type Props<TItem, TItemKey> = {
  id: string
  fetchItems: FetchedOrBeFetchedT<Array<TItem>>
  itemsToCollection: (a: Array<TItem>) => Array<SelectorItemT<TItemKey>>
  selectedItem: TItemKey
  onChangeSelection: (a: TItemKey) => Promise<unknown>
  labels: ComponentLabelsT
  dataTest?: string
  selectorClassName?: string | null
  onFetchError: ((error: Error) => void) | undefined | null
}

// Like `GenericSelector` but with the ability to fetch and update data
export function SelfContainedTeacherSelector<TItem, TItemKey>({
  id,
  labels: {formLabel, loadingText},
  fetchItems,
  onChangeSelection,
  itemsToCollection,
  selectedItem,
  dataTest,
  selectorClassName,
  onFetchError
}: Props<TItem, TItemKey>): React.ReactElement {
  const [items, setItems] = React.useState<Array<TItem>>([])
  const [loading, setLoading] = React.useState(false)
  const [error, setError] = React.useState<Error | undefined | null>(null)

  React.useEffect(() => {
    async function getItems() {
      setError(null)
      switch (fetchItems.tag) {
        case 'fetched':
          setItems(fetchItems.value)
          break
        case 'be-fetched':
          setLoading(true)
          try {
            const items = await fetchItems.fetch()
            setItems(items)
          } catch (err) {
            setError(new HttpError(err as HttpError))
            setItems([])
          } finally {
            setLoading(false)
          }
          break
        default:
          exhaustive(fetchItems as never, 'FetchedOrBeFetchedT')
      }
    }

    getItems()
  }, [fetchItems])

  React.useEffect(() => {
    if (
      error !== null &&
      error !== undefined &&
      onFetchError !== null &&
      onFetchError !== undefined
    ) {
      onFetchError(error)
    }
  }, [onFetchError, error])

  const collection = itemsToCollection(items)

  return (
    <>
      {mmap(
        label => (
          <Label htmlFor={id}>{label}</Label>
        ),
        formLabel
      )}
      {loading && (
        <Selector
          id={id}
          selection={selectedItem}
          collection={[
            createSelectorOptionAsItem(null, loadingText, false, 'math-system-loading-option')
          ]}
          className={cn(teacherSelector, selectorClassName)}
          onChangeSelection={() => {
            /*always disabled*/
          }}
          disabled
          dataTest={dataTest}
        />
      )}
      {!loading && (
        <Selector
          id={id}
          selection={selectedItem}
          collection={collection}
          onChangeSelection={v => {
            setLoading(true)
            onChangeSelection(v).finally(() => setLoading(false))
          }}
          className={cn(teacherSelector, selectorClassName)}
          dataTest={dataTest}
        />
      )}
    </>
  )
}
